import React, { useMemo } from 'react';
import { Box, Button, Card, Chip, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
	AccountCircleRounded,
	BadgeRounded,
	CakeRounded,
	EditRounded,
	ErrorOutlineRounded,
	HomeRounded,
	MailRounded,
	PaymentsRounded,
	PendingRounded,
	PersonRounded,
	PhoneRounded,
	PublicRounded,
	SchoolRounded,
	VerifiedRounded,
	WarningAmberRounded
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Country } from 'country-state-city';
import { AsYouType } from 'mui-tel-input';
import { useAppSelector } from '../../redux/store';
import { RoleName, StripeIdentityVerificationStatus, UserProfile, UserRole } from '../../types/user';
import { formatDate } from '../../utils';
import { resolveEducationJurisdiction } from '../../utils/educationHistory';
import EducationTimeline from '../../components/ui/molecules/EducationTimeline';
import DeleteAccountButton from '../../components/ui/molecules/DeleteAccountButton';
import ProfilePicture from '../../components/ui/atoms/ProfilePicture';

/** Human-readable labels for the roles shown as chips in the identity header. */
const ROLE_LABELS: Partial<Record<RoleName, string>> = {
	[RoleName.OrgnaisationAdmin]: 'Organisation Admin',
	[RoleName.HR]: 'HR',
	[RoleName.Registrar]: 'Registrar',
	[RoleName.AdmissionsOfficer]: 'Admissions Officer',
	[RoleName.Agent]: 'Agent',
	[RoleName.Verifier]: 'Verifier',
	[RoleName.Onboarding]: 'Onboarding',
	[RoleName.Student]: 'Student',
	[RoleName.Employee]: 'Employee',
	[RoleName.BillingAdministrator]: 'Billing Administrator',
	[RoleName.VerifierOnlyOrg]: 'Verifier',
	[RoleName.FamilyOrganisationAdmin]: 'Family Admin',
	[RoleName.FamilyMember]: 'Family Member'
};

/** A tinted, rounded icon badge that leads a section header. */
const SectionIcon = ({ children, tone = 'primary' }: { children: React.ReactNode; tone?: 'primary' | 'error' }) => (
	<Box
		sx={{
			flexShrink: 0,
			width: 40,
			height: 40,
			borderRadius: 2.5,
			display: 'grid',
			placeItems: 'center',
			color: `${tone}.main`,
			bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12)
		}}
	>
		{children}
	</Box>
);

/** Header that leads a group of content within the pane. */
const SectionHeader = ({
	icon,
	title,
	description,
	tone
}: {
	icon: React.ReactNode;
	title: string;
	description?: string;
	tone?: 'primary' | 'error';
}) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1 }}>
		<SectionIcon tone={tone}>{icon}</SectionIcon>
		<Box sx={{ minWidth: 0 }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
				{title}
			</Typography>
			{description && (
				<Typography variant="caption" color="text.secondary">
					{description}
				</Typography>
			)}
		</Box>
	</Box>
);

/** A single read-only field: leading icon, label, then one or more value lines. */
const DetailRow = ({
	icon,
	label,
	lines,
	href
}: {
	icon: React.ReactNode;
	label: string;
	lines: string[];
	href?: string;
}) => (
	<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: { xs: 2, sm: 3 }, py: 2 }}>
		<Box
			sx={{
				flexShrink: 0,
				width: 36,
				height: 36,
				borderRadius: 2,
				display: 'grid',
				placeItems: 'center',
				color: 'primary.main',
				bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
			}}
		>
			{icon}
		</Box>
		<Box sx={{ minWidth: 0 }}>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
				{label}
			</Typography>
			<Box sx={{ mt: 0.25 }}>
				{lines.map((line, index) => (
					<Typography
						key={index}
						{...(href ? { component: 'a', href } : {})}
						sx={{
							fontSize: '0.95rem',
							lineHeight: 1.5,
							wordBreak: 'break-word',
							...(href
								? {
										color: 'inherit',
										textDecoration: 'none',
										'&:hover': { textDecoration: 'underline' }
								  }
								: {})
						}}
					>
						{line}
					</Typography>
				))}
			</Box>
		</Box>
	</Box>
);

/** Maps the Stripe identity status to a header chip; returns null when there's nothing useful to show. */
const getVerification = (status?: StripeIdentityVerificationStatus) => {
	switch (status) {
		case StripeIdentityVerificationStatus.Verified:
			return {
				label: 'Identity verified',
				color: 'success' as const,
				icon: <VerifiedRounded fontSize="small" />
			};
		case StripeIdentityVerificationStatus.Started:
		case StripeIdentityVerificationStatus.Processing:
			return {
				label: 'Verification pending',
				color: 'warning' as const,
				icon: <PendingRounded fontSize="small" />
			};
		case StripeIdentityVerificationStatus.RequiresInput:
			return {
				label: 'Verification needs attention',
				color: 'warning' as const,
				icon: <ErrorOutlineRounded fontSize="small" />
			};
		default:
			return null;
	}
};

const Profile = () => {
	const navigate = useNavigate();
	const profile = useAppSelector((state) => state.user.currentUser);
	const {
		firstName,
		lastName,
		preferredName,
		email,
		phone,
		citizenship,
		address,
		dateOfBirth,
		educationHistory,
		currency,
		profilePicture,
		roles,
		stripeIdentityVerified
	} = (profile as UserProfile) ?? ({} as Partial<UserProfile>);

	// De-duplicated, human-readable role labels for the identity header chips.
	const roleLabels = useMemo(() => {
		const seen = new Set<string>();
		const labels: string[] = [];
		(roles ?? []).forEach((role: UserRole) => {
			const label = ROLE_LABELS[role.roleName] ?? role.roleName;
			if (!seen.has(label)) {
				seen.add(label);
				labels.push(label);
			}
		});
		return labels;
	}, [roles]);

	// Address rendered as discrete lines (comma-safe — never join/split on commas).
	const addressLines = useMemo(() => {
		if (!address) return [];
		const clean = (value?: string) => value?.trim() || '';
		const lines: string[] = [];
		[address.line1, address.line2].forEach((line) => {
			if (clean(line)) lines.push(clean(line));
		});
		const locality = [address.city, address.state].map(clean).filter(Boolean).join(', ');
		if (locality) lines.push(locality);
		if (clean(address.zip)) lines.push(clean(address.zip));
		if (clean(address.country)) lines.push(clean(address.country));
		return lines;
	}, [address]);

	const employeeId = roles?.find(
		(role: UserRole) => role.roleName === RoleName.Employee && role.serialNumber
	)?.serialNumber;
	const studentId = roles?.find(
		(role: UserRole) => role.roleName === RoleName.Student && role.serialNumber
	)?.serialNumber;

	// The detail fields, in display order — only those with a value are shown.
	const fields = useMemo(() => {
		const list: { icon: React.ReactNode; label: string; lines: string[]; href?: string }[] = [];
		if (preferredName)
			list.push({
				icon: <AccountCircleRounded fontSize="small" />,
				label: 'Preferred name',
				lines: [preferredName]
			});
		if (email) list.push({ icon: <MailRounded fontSize="small" />, label: 'Email', lines: [email] });
		if (phone) {
			const parsed = new AsYouType();
			parsed.input(phone);
			const parsedNumber = parsed.getNumber();
			const phoneFlag = parsedNumber?.country ? Country.getCountryByCode(parsedNumber.country)?.flag : undefined;
			const formattedPhone = parsedNumber?.formatInternational() || phone;
			list.push({
				icon: <PhoneRounded fontSize="small" />,
				label: 'Phone',
				lines: [phoneFlag ? `${phoneFlag} ${formattedPhone}` : formattedPhone],
				href: `tel:${phone.replace(/[^+\d]/g, '')}`
			});
		}
		if (citizenship) {
			const flag = Country.getAllCountries().find((c) => c.name === citizenship)?.flag;
			list.push({
				icon: <PublicRounded fontSize="small" />,
				label: 'Citizenship',
				lines: [flag ? `${flag} ${citizenship}` : citizenship]
			});
		}
		if (dateOfBirth)
			list.push({
				icon: <CakeRounded fontSize="small" />,
				label: 'Date of birth',
				lines: [formatDate(dateOfBirth, 'D MMMM YYYY')]
			});
		if (currency)
			list.push({
				icon: <PaymentsRounded fontSize="small" />,
				label: 'Currency',
				lines: [currency.toUpperCase()]
			});
		if (addressLines.length)
			list.push({ icon: <HomeRounded fontSize="small" />, label: 'Address', lines: addressLines });
		if (employeeId)
			list.push({ icon: <BadgeRounded fontSize="small" />, label: 'Employee ID', lines: [employeeId] });
		if (studentId) list.push({ icon: <SchoolRounded fontSize="small" />, label: 'Student ID', lines: [studentId] });
		return list;
	}, [
		preferredName,
		email,
		phone,
		citizenship,
		dateOfBirth,
		educationHistory,
		currency,
		addressLines,
		employeeId,
		studentId
	]);

	const verification = getVerification(stripeIdentityVerified);

	return (
		<Box sx={{ pt: 2, width: { xs: '100%', md: '90%' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
			<Card sx={{ p: 0, overflow: 'hidden', '&:hover': { transform: 'none' } }}>
				{/* Identity header — avatar, name, email, role chips and edit action. */}
				<Box
					sx={{
						display: 'flex',
						alignItems: { xs: 'flex-start', sm: 'center' },
						justifyContent: 'space-between',
						flexDirection: { xs: 'column', sm: 'row' },
						gap: 2,
						px: { xs: 2, sm: 3 },
						py: 2.5
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
						<ProfilePicture
							aria-label="account of current user"
							sx={{ width: '4.5rem', height: '4.5rem', fontSize: '2.5rem' }}
							image={profilePicture}
							profile={profile}
						/>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
								{firstName} {lastName}
							</Typography>
							{email && (
								<Typography variant="body2" color="text.secondary" noWrap>
									{email}
								</Typography>
							)}
							{(verification || roleLabels.length > 0) && (
								<Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
									{verification && (
										<Chip
											icon={verification.icon}
											label={verification.label}
											size="small"
											color={verification.color}
										/>
									)}
									{roleLabels.map((label) => (
										<Chip
											key={label}
											label={label}
											size="small"
											color="primary"
											variant="outlined"
										/>
									))}
								</Stack>
							)}
						</Box>
					</Box>
					<Button
						variant="contained"
						startIcon={<EditRounded sx={{ color: 'common.white' }} />}
						onClick={() => navigate('/profile/edit')}
						sx={{
							flexShrink: 0,
							width: { xs: '100%', sm: 'auto' },
							px: 3,
							py: 1,
							borderRadius: 999,
							fontWeight: 600,
							textTransform: 'none',
							color: 'common.white',
							bgcolor: 'primary.main',
							border: 'none',
							boxShadow: (theme) => `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
							'&:hover': {
								bgcolor: 'primary.dark',
								border: 'none',
								boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.4)}`,
								transform: 'translateY(-2px)'
							}
						}}
					>
						Edit Profile
					</Button>
				</Box>

				<Divider />

				{/* Personal details. */}
				<SectionHeader
					icon={<PersonRounded />}
					title="Personal details"
					description="The information held against your account."
				/>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
						'& > *': { borderTop: '1px solid', borderColor: 'divider' },
						'& > *:nth-of-type(even)': { borderLeft: { sm: '1px solid' }, borderColor: 'divider' }
					}}
				>
					{fields.map((field, index) => (
						<DetailRow
							key={index}
							icon={field.icon}
							label={field.label}
							lines={field.lines}
							href={field.href}
						/>
					))}
				</Box>

				{/* Education history (PTEC-517) — same timeline as the editor,
				    read-only; the derived most-recent school is highlighted. */}
				{educationHistory && educationHistory.length > 0 && (
					<>
						<Divider />
						<SectionHeader
							icon={<SchoolRounded />}
							title="Education"
							description="Your education history — most recent first."
						/>
						<Box sx={{ px: { xs: 2, sm: 3 }, pb: 1.5 }}>
							<EducationTimeline
								entries={educationHistory}
								jurisdiction={resolveEducationJurisdiction({
									isoCountryCode: address?.isoCountryCode,
									citizenship
								})}
							/>
						</Box>
					</>
				)}

				<Divider />

				{/* Danger zone — account deletion. */}
				<SectionHeader
					tone="error"
					icon={<WarningAmberRounded />}
					title="Danger zone"
					description="Irreversible actions for your account."
				/>
				<Box
					sx={{
						display: 'flex',
						alignItems: { xs: 'flex-start', sm: 'center' },
						justifyContent: 'space-between',
						flexDirection: { xs: 'column', sm: 'row' },
						gap: { xs: 1.5, sm: 3 },
						px: { xs: 2, sm: 3 },
						pt: 1,
						pb: 2.5,
						bgcolor: (theme) => alpha(theme.palette.error.main, 0.04)
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
							Delete account
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
							Permanently delete your account and all associated data. This cannot be undone.
						</Typography>
					</Box>
					<Box sx={{ flexShrink: 0 }}>
						<DeleteAccountButton profile={profile} widthControl />
					</Box>
				</Box>
			</Card>
		</Box>
	);
};

export default Profile;
